// ─── Research Readiness Panel ────────────────────────────────────────────────
// Replaces the raw validation error panel with an academic review experience.
// Groups refinement suggestions by category. No raw traces or engine names shown.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, Loader2, CheckCircle, BookOpen, BarChart2, Quote, FileDown, MessageSquare } from 'lucide-react'

// ── Category definitions ──────────────────────────────────────────────────────

const CATEGORIES = {
  research:  { label: 'Research Completeness',   icon: BarChart2,    color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  figures:   { label: 'Figures & Tables',         icon: BookOpen,     color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  citations: { label: 'Citation Quality',          icon: Quote,        color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
  export:    { label: 'Export Readiness',          icon: FileDown,     color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  review:    { label: 'Review Notes',              icon: MessageSquare,color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
}

// ── Map raw validator message → academic category ─────────────────────────────

function categorise(issue) {
  const m = (issue.message || '').toLowerCase()
  const t = (issue.type   || '').toLowerCase()
  if (/figure|table|lof|lot|anchor|caption|numbering/i.test(m + t))  return 'figures'
  if (/citation|reference|bibliography|apa|harvard|ibid/i.test(m + t)) return 'citations'
  if (/placeholder|chapter|missing|corrupt|structure/i.test(m + t))   return 'export'
  if (/research|regression|statistic|coefficient|data|sample/i.test(m + t)) return 'research'
  return 'review'
}

// ── Rephrase validator messages into academic language ────────────────────────

function academicise(message) {
  return message
    .replace(/critical error/gi,        'item requiring attention')
    .replace(/validation failed/gi,     'additional refinement recommended')
    .replace(/export blocked/gi,        'final review required before export')
    .replace(/must be fixed/gi,         'recommended before final submission')
    .replace(/broken/gi,                'may need review')
    .replace(/invalid/gi,               'may benefit from revision')
    .replace(/corrupt/gi,               'may require attention')
    .replace(/error/gi,                 'note')
    .replace(/failed/gi,                'could not be confirmed')
    .replace(/missing/gi,               'not yet provided')
    .replace(/WARNING:/gi,              '')
    .replace(/ERROR:/gi,                '')
    .trim()
}

// ── Readiness score ring ──────────────────────────────────────────────────────

function ReadinessRing({ score }) {
  const color = score >= 80 ? '#059669' : score >= 60 ? '#d97706' : '#2563eb'
  const label = score >= 80 ? 'Ready for export'
              : score >= 60 ? 'Refinement suggested'
              : 'Review recommended'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{ width: 68, height: 68, borderRadius: '50%', border: `5px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${color}12` }}>
        <span style={{ fontSize: 19, fontWeight: 800, color }}>{score}</span>
      </div>
      <span style={{ fontSize: 10, fontWeight: 600, color, textAlign: 'center', maxWidth: 72 }}>{label}</span>
    </div>
  )
}

// ── Category section ──────────────────────────────────────────────────────────

function CategorySection({ categoryKey, issues }) {
  const [open, setOpen] = useState(true)
  const cfg  = CATEGORIES[categoryKey]
  const Icon = cfg.icon

  return (
    <div style={{ border: `1px solid ${cfg.border}`, borderRadius: 8, marginBottom: 8, overflow: 'hidden', background: cfg.bg }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0.75rem', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <Icon style={{ width: 13, height: 13, color: cfg.color, flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {cfg.label}
        </span>
        <span style={{ fontSize: 10, fontWeight: 600, color: cfg.color, background: `${cfg.color}18`, borderRadius: 10, padding: '1px 7px' }}>
          {issues.length}
        </span>
        {open
          ? <ChevronDown style={{ width: 11, height: 11, color: cfg.color }} />
          : <ChevronRight style={{ width: 11, height: 11, color: cfg.color }} />}
      </button>

      {open && (
        <div style={{ borderTop: `1px solid ${cfg.border}`, padding: '0.5rem 0.75rem 0.5rem 2rem' }}>
          {issues.map((issue, i) => (
            <div key={i} style={{ marginBottom: i < issues.length - 1 ? 8 : 0 }}>
              <p style={{ fontSize: 12, color: '#374151', margin: 0, lineHeight: 1.5 }}>
                {academicise(issue.message)}
              </p>
              {issue.repair && (
                <Link
                  to={issue.repair.href}
                  style={{ fontSize: 11, fontWeight: 600, color: cfg.color, textDecoration: 'none', display: 'inline-block', marginTop: 3 }}
                >
                  {issue.repair.label} →
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Export state badge ────────────────────────────────────────────────────────

function ExportStateBadge({ state }) {
  const cfg = {
    READY:                { label: 'Ready for Export',         color: '#059669', bg: '#d1fae5', border: '#a7f3d0' },
    READY_WITH_ADVISORIES:{ label: 'Ready — Advisories Present', color: '#d97706', bg: '#fef3c7', border: '#fde68a' },
    REVIEW_RECOMMENDED:   { label: 'Final Review Recommended', color: '#2563eb', bg: '#dbeafe', border: '#bfdbfe' },
  }[state] || { label: state, color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' }

  return (
    <span style={{ fontSize: 10, fontWeight: 700, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10, padding: '2px 8px', letterSpacing: '0.04em' }}>
      {cfg.label}
    </span>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * @param {{ validation: ValidationResult | null, loading: boolean, onRecheck: () => void }} props
 */
export default function ExportReadinessPanel({ validation, loading, onRecheck }) {
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '2rem', background: '#f8fafc', borderRadius: 12, border: '1px solid #DDE4EF' }}>
        <Loader2 style={{ width: 22, height: 22, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Reviewing thesis…</p>
      </div>
    )
  }

  if (!validation) return null

  const { passed, errors = [], warnings = [], exportReadinessScore = 0, details } = validation

  // Merge all issues into one pool, classify, and group
  const allIssues = [
    ...errors.map(e => ({ ...e, _blocking: true })),
    ...warnings.map(w => ({ ...w, _blocking: false })),
  ]
  const grouped = {}
  for (const issue of allIssues) {
    const cat = categorise(issue)
    if (!grouped[cat]) grouped[cat] = []
    grouped[cat].push(issue)
  }

  const blockingCount  = errors.length
  const advisoryCount  = warnings.length
  const exportState    = blockingCount === 0 && advisoryCount === 0 ? 'READY'
                       : blockingCount === 0 ? 'READY_WITH_ADVISORIES'
                       : 'REVIEW_RECOMMENDED'

  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #DDE4EF', borderRadius: 12, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #DDE4EF', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>Research Readiness</h3>
          <div style={{ marginTop: 4 }}>
            <ExportStateBadge state={exportState} />
          </div>
        </div>
        <ReadinessRing score={exportReadinessScore} />
      </div>

      {/* Overview row */}
      <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid #DDE4EF', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '0.5rem' }}>
        {[
          { label: 'Chapters',      value: details?.chapterCount || 0,  ok: (details?.chapterCount || 0) > 0 },
          { label: 'Figures',       value: details?.figureCount  || 0,  ok: true },
          { label: 'Tables',        value: details?.tableCount   || 0,  ok: true },
          { label: 'In Progress',   value: details?.draftCount   || 0,  ok: (details?.draftCount || 0) === 0 },
        ].map(({ label, value, ok }) => (
          <div key={label} style={{ background: ok ? 'rgba(5,150,105,0.06)' : 'rgba(217,119,6,0.06)', border: `1px solid ${ok ? '#a7f3d0' : '#fde68a'}`, borderRadius: 8, padding: '0.4rem 0.5rem', textAlign: 'center' }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: ok ? '#059669' : '#d97706', margin: 0 }}>{value}</p>
            <p style={{ fontSize: 9, color: '#64748b', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Grouped suggestions */}
      {allIssues.length > 0 ? (
        <div style={{ padding: '0.75rem 1.25rem' }}>
          {Object.entries(grouped).map(([cat, issues]) => (
            <CategorySection key={cat} categoryKey={cat} issues={issues} />
          ))}
        </div>
      ) : (
        <div style={{ padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle style={{ width: 18, height: 18, color: '#10b981' }} />
          <span style={{ fontSize: 13, color: '#059669', fontWeight: 600 }}>
            All checks clear — this thesis is ready for final export.
          </span>
        </div>
      )}

      {/* Recheck */}
      <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #DDE4EF', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button
          onClick={onRecheck}
          style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          Re-run review
        </button>
        {allIssues.length > 0 && (
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            {blockingCount > 0 ? `${blockingCount} item${blockingCount > 1 ? 's' : ''} for attention` : `${advisoryCount} advisory note${advisoryCount > 1 ? 's' : ''}`}
          </span>
        )}
      </div>
    </div>
  )
}
